import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

export type Theme = 'light' | 'dark' | 'system'

export type ResolvedTheme = 'light' | 'dark'

type ThemeContextValue = {
    theme: Theme
    resolvedTheme: ResolvedTheme
    setTheme: (theme: Theme) => void
    toggleTheme: () => void
}

interface ThemeProviderProps {
    children: ReactNode
}

// Must match the key read by the inline script in _document.tsx
const STORAGE_KEY = 'arunika-theme'

const ThemeContext = createContext<ThemeContextValue>({
    theme: 'system',
    resolvedTheme: 'light',
    setTheme: () => undefined,
    toggleTheme: () => undefined
})

const applyTheme = (resolved: ResolvedTheme): void => {
    const root = document.documentElement

    root.classList.add('theme-switching')
    root.classList.toggle('dark', resolved === 'dark')
    root.style.colorScheme = resolved

    // Read a computed style so the new colors land before transitions come back
    window.getComputedStyle(root).getPropertyValue('color')
    window.setTimeout(() => root.classList.remove('theme-switching'), 1)
}

export const ThemeProvider: FC<ThemeProviderProps> = ({ children }): ReactNode => {
    const [theme, setThemeState] = useState<Theme>('system')
    const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light')
    const [ready, setReady] = useState(false)

    useEffect(() => {
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY)
            if (stored === 'light' || stored === 'dark' || stored === 'system') setThemeState(stored)
        }
        catch {
            // Storage can be unavailable in private browsing; keep the default
        }

        setResolvedTheme(document.documentElement.classList.contains('dark') ? 'dark' : 'light')
        setReady(true)
    }, [])

    useEffect(() => {
        if (!ready) return

        const media = window.matchMedia('(prefers-color-scheme: dark)')

        const update = (): void => {
            const next: ResolvedTheme = theme === 'dark' || (theme === 'system' && media.matches) ? 'dark' : 'light'
            const current: ResolvedTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light'

            if (next !== current) applyTheme(next)
            setResolvedTheme(next)
        }

        update()

        if (theme !== 'system') return

        media.addEventListener('change', update)
        return () => media.removeEventListener('change', update)
    }, [theme, ready])

    const setTheme = useCallback((next: Theme) => {
        setThemeState(next)

        try {
            window.localStorage.setItem(STORAGE_KEY, next)
        }
        catch {
            // Ignore: the choice still applies for this visit
        }
    }, [])

    const toggleTheme = useCallback(() => {
        setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
    }, [resolvedTheme, setTheme])

    const value = useMemo(() => ({ theme, resolvedTheme, setTheme, toggleTheme }), [theme, resolvedTheme, setTheme, toggleTheme])

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    )
}

export const useTheme = (): ThemeContextValue => useContext(ThemeContext)
